import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import './i18n';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { ResellerCompanyProvider } from './contexts/ResellerCompanyContext';
import { SuperAdminCompanyProvider } from './contexts/SuperAdminCompanyContext';
import { WebSocketProvider } from './contexts/WebSocketContext';

// Components
import Layout from './components/Layout';
import ProtectedModule from './components/ProtectedModule';
import Login from './pages/Login';
import Contact from './pages/Contact';
import Dashboard from './pages/Dashboard';
import Machines from './pages/Machines';
import Employees from './pages/Employees';
import EmployeeReport from './pages/EmployeeReport';
import Qualities from './pages/Qualities';
import Purchases from './pages/Purchases';
import PurchaseCategories from './pages/PurchaseCategories';
import Clients from './pages/Clients';
import Sales from './pages/Sales';
import CreateSale from './pages/CreateSale';
import Beams from './pages/Beams';
import Production from './pages/Production';
import Salary from './pages/Salary';
import ChipManagement from './pages/ChipManagement';
import CompanyManagement from './pages/CompanyManagement';
import GPSManagement from './pages/GPSManagement';
import RoleManagement from './pages/RoleManagement';
import BeamLog from './pages/BeamLog';
import CostCalculating from './pages/CostCalculating';
import Stock from './pages/Stock';
import Profile from './pages/Profile';
import MeterEntries from './pages/MeterEntries';
import TakaInventory from './pages/TakaInventory';
import Challan from './pages/Challan';
import BeamMaster from './pages/BeamMaster';
import YarnMaster from './pages/YarnMaster';
import YarnDashboard from './pages/YarnDashboard';
import YarnOutward from './pages/YarnOutward';
import BillPage from './pages/Bill';
import FingerprintManagement from './pages/FingerprintManagement';
import FingerprintDevices from './pages/FingerprintDevices';
import EmployeeShift from './pages/EmployeeShift';
import ShiftManagement from './pages/ShiftManagement';
import Attendance from './pages/Attendance';
import MyAttendance from './pages/MyAttendance';
import AttendanceApprovals from './pages/AttendanceApprovals';
import RapierMachine from './pages/RapierMachine';
import Settings from './pages/Settings';

// Context
import { useAuth } from './contexts/AuthContext';
import { getHomeRoute } from './utils/homeRoute';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Default landing route — shared with the Login page via utils/homeRoute
const getMachineHomePage = (user: any) => getHomeRoute(user);

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return user ? <Navigate to={getMachineHomePage(user)} replace /> : <>{children}</>;
};

// Dynamic redirect based on user's machine access flags
const MachineHomeRedirect: React.FC = () => {
  const { user } = useAuth();
  return <Navigate to={getMachineHomePage(user)} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CompanyProvider>
          <ResellerCompanyProvider>
          <SuperAdminCompanyProvider>
          <WebSocketProvider>
          <Router>
          <div className="App">
            <Toaster
              position="top-right"
              // Above every modal, incl. the portaled Stop Events modal (z 100000)
              // and the TV overlay (z 99999), so toasts are never hidden.
              containerStyle={{ zIndex: 100001 }}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10B981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#EF4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/contact"
                element={
                  <PublicRoute>
                    <Contact />
                  </PublicRoute>
                }
              />
              {/* Legacy /register now points to the contact form */}
              <Route path="/register" element={<Navigate to="/contact" replace />} />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<MachineHomeRedirect />} />
                <Route path="dashboard" element={
                  <ProtectedModule moduleName="dashboard" allowRoles={['ADMIN']}>
                    <Dashboard />
                  </ProtectedModule>
                } />
                <Route path="profile" element={<Profile />} />
                <Route path="company-management" element={
                  <ProtectedModule moduleName="company-management">
                    <CompanyManagement />
                  </ProtectedModule>
                } />
                <Route path="chip-management" element={
                  <ProtectedModule moduleName="chip-management">
                    <ChipManagement />
                  </ProtectedModule>
                } />
                <Route path="gps-management" element={
                  <ProtectedModule moduleName="gps-management">
                    <GPSManagement />
                  </ProtectedModule>
                } />
                <Route path="role-management" element={
                  <ProtectedModule moduleName="role-management">
                    <RoleManagement />
                  </ProtectedModule>
                } />
                <Route path="cost-calculating" element={
                  <ProtectedModule moduleName="cost-calculating">
                    <CostCalculating />
                  </ProtectedModule>
                } />
                <Route path="stock" element={
                  <ProtectedModule moduleName="stock">
                    <Stock />
                  </ProtectedModule>
                } />
                
                {/* Textile Module Routes */}
                <Route path="machines" element={
                  <ProtectedModule moduleName="machines">
                    <Machines />
                  </ProtectedModule>
                } />
                <Route path="employees" element={
                  <ProtectedModule moduleName="employees">
                    <Employees />
                  </ProtectedModule>
                } />
                <Route path="qualities" element={
                  <ProtectedModule moduleName="qualities">
                    <Qualities />
                  </ProtectedModule>
                } />
                <Route path="purchases" element={
                  <ProtectedModule moduleName="purchases">
                    <Purchases />
                  </ProtectedModule>
                } />
                <Route path="purchase-categories" element={
                  <ProtectedModule moduleName="purchases">
                    <PurchaseCategories />
                  </ProtectedModule>
                } />
                <Route path="clients" element={
                  <ProtectedModule moduleName="clients">
                    <Clients />
                  </ProtectedModule>
                } />
                <Route path="sales" element={
                  <ProtectedModule moduleName="sales">
                    <Sales />
                  </ProtectedModule>
                } />
                <Route path="sales/create" element={
                  <ProtectedModule moduleName="sales">
                    <CreateSale />
                  </ProtectedModule>
                } />
                <Route path="beams" element={
                  <ProtectedModule moduleName="beams">
                 <BeamLog />
                  </ProtectedModule>
                } />
                <Route path="beam-log" element={
                  <ProtectedModule moduleName="beams">
                    <BeamLog />
                  </ProtectedModule>
                } />
                <Route path="production" element={
                  <ProtectedModule moduleName="production">
                    <Production />
                  </ProtectedModule>
                } />
                <Route path="salary" element={
                  <ProtectedModule moduleName="salary">
                    <Salary />
                  </ProtectedModule>
                } />
                <Route path="meter-entries" element={
                  <ProtectedModule moduleName="production">
                    <MeterEntries />
                  </ProtectedModule>
                } />
                <Route path="taka-inventory" element={
                  <ProtectedModule moduleName="taka-inventory" permission={['canView', 'canCreate']}>
                    <TakaInventory />
                  </ProtectedModule>
                } />
                <Route path="challan" element={
                  <ProtectedModule moduleName="stock">
                    <Challan />
                  </ProtectedModule>
                } />
                <Route path="beam-master" element={
                  <ProtectedModule moduleName="beam-master">
                    <BeamMaster />
                  </ProtectedModule>
                } />
                <Route path="yarn-master" element={
                  <ProtectedModule moduleName="yarn-inward">
                    <YarnMaster />
                  </ProtectedModule>
                } />
                <Route path="yarn-dashboard" element={
                  <ProtectedModule moduleName="yarn-dashboard">
                    <YarnDashboard />
                  </ProtectedModule>
                } />
                <Route path="yarn-outward" element={
                  <ProtectedModule moduleName="yarn-outward">
                    <YarnOutward />
                  </ProtectedModule>
                } />
                <Route path="bill" element={
                  <ProtectedModule moduleName="stock">
                    <BillPage />
                  </ProtectedModule>
                } />
                <Route path="employee-report" element={
                  <ProtectedModule moduleName="salary">
                    <EmployeeReport />
                  </ProtectedModule>
                } />
                <Route path="fingerprint-management" element={
                  <ProtectedModule moduleName="fingerprint-management">
                    <FingerprintManagement />
                  </ProtectedModule>
                } />
                <Route path="fingerprint-devices" element={
                  <FingerprintDevices />
                } />
                <Route path="employee-shift" element={
                  <EmployeeShift />
                } />
                <Route path="shift-management" element={
                  <ProtectedModule moduleName="fingerprint-management">
                    <ShiftManagement />
                  </ProtectedModule>
                } />
                <Route path="attendance" element={
                  <Attendance />
                } />
                <Route path="my-attendance" element={<MyAttendance />} />
                {/* Approvals page self-gates on canApprove (shows a notice otherwise),
                    so the trusted-manager flag alone unlocks it — no module wrapper. */}
                <Route path="attendance-approvals" element={<AttendanceApprovals />} />
                {/* Salary / People sections are addressable as sub-routes so the sidebar
                    submenu can highlight the active section (page reads :tab). */}
                <Route path="attendance-approvals/:tab" element={<AttendanceApprovals />} />
                <Route path="rapier-machine" element={
                  <ProtectedModule moduleName="machines">
                    <RapierMachine />
                  </ProtectedModule>
                } />
                <Route path="settings" element={<Settings />} />
              </Route>

              {/* Catch all route */}
              <Route path="*" element={<MachineHomeRedirect />} />
            </Routes>
          </div>
        </Router>
          </WebSocketProvider>
          </SuperAdminCompanyProvider>
          </ResellerCompanyProvider>
        </CompanyProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
